import * as XLSX from 'xlsx'
import isNil from 'lodash/isNil'
import {
  ClientOptionDto,
  InvoiceTemplate,
} from '../../../api/dto/invoice-delivery.dto'

export interface ParsedInvoiceSheet {
  companyName: string | null
  /** On an OE sheet, the whole Open Enrollment amount. */
  totalDue: number | null
  /** OE1 part 1, the amount its email states. */
  newEnrollments?: number | null
  /** OE1 part 2. */
  rollover?: number | null
  /** The amount an OE2 email states. */
  remainingDue?: number | null
  /** OE2: what OE1 already collected. */
  paid?: number | null
}

type SheetAmountField = Exclude<keyof ParsedInvoiceSheet, 'companyName'>

/** Same labels the server parser reads; the lowest matching row wins. */
const AMOUNT_LABELS: Record<string, SheetAmountField> = {
  'total due': 'totalDue',
  'new enrollments/changes': 'newEnrollments',
  'rollover plan costs & fees': 'rollover',
  'remaining due': 'remainingDue',
  paid: 'paid',
}

/** In January a final sheet carries Remaining due or Paid, so it starts as OE2. */
export const guessInvoiceTemplate = (
  parsed: ParsedInvoiceSheet | undefined,
  fallback: InvoiceTemplate
): InvoiceTemplate => {
  if (fallback !== InvoiceTemplate.OE_FIRST || isNil(parsed)) return fallback
  return isNil(parsed.remainingDue) && isNil(parsed.paid)
    ? fallback
    : InvoiceTemplate.OE_FINAL
}

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const parsed = parseFloat(value.replace(/[^0-9.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Reads the cells the upload preview needs, the same way the server does: the
 * company from A1 of the "Invoice" sheet (else the first sheet), and each
 * amount row's value from column B.
 */
export const parseInvoiceSheet = async (
  file: File
): Promise<ParsedInvoiceSheet> => {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' })
  const sheet =
    workbook.Sheets.Invoice ?? workbook.Sheets[workbook.SheetNames[0]]
  if (isNil(sheet)) return { companyName: null, totalDue: null }

  const companyCell = sheet.A1?.v
  const companyName = isNil(companyCell) ? null : String(companyCell).trim()

  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    blankrows: false,
  })
  const amounts: Partial<Record<SheetAmountField, number | null>> = {}
  for (const row of rows) {
    const label = String(row[0] ?? '')
      .trim()
      .toLowerCase()
      .replace(/:$/, '')
    const field = AMOUNT_LABELS[label]
    if (!isNil(field)) amounts[field] = toNumber(row[1])
  }
  return {
    companyName: companyName || null,
    totalDue: amounts.totalDue ?? null,
    newEnrollments: amounts.newEnrollments ?? null,
    rollover: amounts.rollover ?? null,
    remainingDue: amounts.remainingDue ?? null,
    paid: amounts.paid ?? null,
  }
}

/** Mirrors the server's match: exact name first, then either name containing the other. */
export const matchClient = (
  companyName: string | null,
  clients: ClientOptionDto[]
): ClientOptionDto | null => {
  if (isNil(companyName) || companyName.length === 0) return null
  const parsed = companyName.toLowerCase()
  const exact = clients.find((c) => c.name.toLowerCase() === parsed)
  if (!isNil(exact)) return exact
  return (
    clients.find((c) => {
      const name = c.name.toLowerCase()
      return name.length > 0 && (parsed.includes(name) || name.includes(parsed))
    }) ?? null
  )
}
